/**
 * CSV Export Utility for Pending Channels
 * Handles formatting and downloading channel data as CSV
 */

import { PendingChannel } from '../types/pendingChannels';

/**
 * Format value for CSV (handle quotes, newlines, etc.)
 */
const escapeCSVField = (field: any): string => {
  if (field === null || field === undefined) {
    return '';
  }

  const stringValue = String(field);

  // If field contains comma, newline, or quote, wrap in quotes and escape quotes
  if (stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"')) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }

  return stringValue;
};

/**
 * Format date to readable string
 */
const formatDateForCSV = (date: any): string => {
  if (!date) return '';
  try {
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
  } catch {
    return '';
  }
};

/**
 * Format number with K/M/B suffix
 */
const formatNumberForCSV = (num: any): string => {
  if (num === undefined || num === null || num === '') {
    return '';
  }

  let value: number;
  try {
    value = typeof num === 'string' ? parseFloat(num) : Number(num);
  } catch {
    return String(num);
  }

  if (isNaN(value)) {
    return String(num);
  }

  // Return raw number for CSV (spreadsheets handle large numbers)
  return value.toLocaleString();
};

/**
 * Generate CSV content from pending channels
 */
export const generateChannelsCSV = (channels: (PendingChannel & { _id: string })[]): string => {
  if (channels.length === 0) {
    return '';
  }

  // Define CSV headers
  const headers = [
    'Channel ID',
    'Channel Name',
    'Email',
    'Subscribers',
    'Playlists',
    'Discovery Method',
    'Discovery Date',
    'Workflow Status',
    'Outreach Status',
    'Twitter',
    'Instagram',
    'Website',
    'Notes',
  ];

  // Convert headers to CSV row
  const headerRow = headers.map(escapeCSVField).join(',');

  // Convert each channel to CSV row
  const dataRows = channels.map((channel) => {
    const row = [
      escapeCSVField(channel.id || channel._id),
      escapeCSVField(channel.title),
      escapeCSVField(channel.email),
      escapeCSVField(formatNumberForCSV(channel.subscriber_count)),
      escapeCSVField(formatNumberForCSV(channel.playlist_count)),
      escapeCSVField(channel.discovery_method),
      escapeCSVField(formatDateForCSV(channel.discovery_date)),
      escapeCSVField(channel.workflow_status),
      escapeCSVField(channel.outreach_status),
      escapeCSVField(channel.socialLinks?.twitter || ''),
      escapeCSVField(channel.socialLinks?.instagram || ''),
      escapeCSVField(channel.socialLinks?.website || channel.website || ''),
      escapeCSVField(channel.notes || ''),
    ];
    return row.join(',');
  });

  // Combine header and data rows with BOM for UTF-8 encoding (ensures Excel handles special chars)
  const csvContent = [headerRow, ...dataRows].join('\n');

  // Add UTF-8 BOM for proper Excel encoding
  return '\uFEFF' + csvContent;
};

/**
 * Download CSV file to user's computer
 */
export const downloadCSV = (content: string, filename: string = 'pending-channels.csv'): void => {
  if (!content) {
    console.warn('No content to download');
    return;
  }

  try {
    // Create blob with UTF-8 encoding
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });

    // Create temporary URL
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    // Trigger download
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up URL
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading CSV:', error);
    throw new Error('Failed to download CSV file');
  }
};

/**
 * Export channels to CSV file (combined function)
 */
export const exportChannelsToCSV = (
  channels: (PendingChannel & { _id: string })[],
  filename?: string
): void => {
  const csv = generateChannelsCSV(channels);
  downloadCSV(csv, filename);
};
